import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star, Loader2, Landmark, MapPin } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { imgUrl } from "@/pages/admin/PropertiesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const token = localStorage.getItem("rk_admin_token");
    try {
      const { data } = await axios.get(`${API}/admin/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch {
      toast.error("Could not load projects.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (p, changes, msg) => {
    const token = localStorage.getItem("rk_admin_token");
    setBusy(p.id);
    try {
      await axios.put(`${API}/admin/projects/${p.id}`, { ...p, ...changes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((list) => list.map((x) => (x.id === p.id ? { ...x, ...changes } : x)));
      toast.success(msg);
    } catch {
      toast.error("Update failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" permanently? This cannot be undone.`)) return;
    const token = localStorage.getItem("rk_admin_token");
    setBusy(p.id);
    try {
      await axios.delete(`${API}/admin/projects/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems((list) => list.filter((x) => x.id !== p.id));
      toast.success("Project deleted.");
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (items === null)
    return <div className="py-16 flex justify-center text-ink-soft"><Loader2 className="animate-spin" size={26} /></div>;

  return (
    <div data-testid="projects-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">Projects</h2>
          <p className="mt-1 text-sm text-ink-soft">
            {items.length} {items.length === 1 ? "project" : "projects"} · Featured + published projects appear on the homepage.
          </p>
        </div>
        <Link
          to="/admin/projects/new"
          data-testid="add-project-button"
          className="bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-5 py-3 text-[14px] font-semibold inline-flex items-center gap-2 shadow-md shadow-gold/25"
        >
          <Plus size={16} /> Add Project
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 bg-white rounded-xl border border-line p-14 text-center" data-testid="projects-admin-empty-state">
          <Landmark size={30} className="mx-auto text-gold" />
          <p className="mt-4 font-display text-xl text-navy">No projects yet</p>
          <p className="mt-1.5 text-sm text-ink-soft">Add your first project — mark it Featured and Published to show it on the homepage.</p>
          <Link to="/admin/projects/new" className="mt-6 inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold">
            <Plus size={15} /> Add Project
          </Link>
        </div>
      ) : (
        <div className="mt-8 space-y-4" data-testid="admin-projects-list">
          {items.map((p) => (
            <article key={p.id} className="bg-white rounded-xl border border-line p-4 sm:p-5 flex flex-col sm:flex-row gap-4 sm:gap-5 hover:border-gold-soft transition-colors" data-testid={`admin-project-${p.id}`}>
              <div className="w-full sm:w-36 h-40 sm:h-28 rounded-lg overflow-hidden bg-warm border border-line shrink-0">
                {p.images?.length ? (
                  <img src={imgUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-soft/50"><Landmark size={22} /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-navy leading-snug">{p.name}</h3>
                  {p.featured && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-gold/20 text-navy border border-gold/50 rounded px-2 py-0.5">
                      <Star size={10} /> Featured
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-[0.1em] rounded px-2 py-0.5 border ${p.published ? "bg-navy text-white border-navy" : "bg-line/50 text-ink-soft border-line"}`} data-testid={`admin-project-status-${p.id}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="mt-1 text-[13px] text-ink-soft flex items-center gap-1.5">
                  <MapPin size={12} className="text-gold" /> {p.location} · {p.status}
                </p>
                <p className="mt-1.5 text-[13px] font-medium text-ink/80">
                  {[p.propertyType, p.bhkOptions].filter(Boolean).join(" · ")}
                  {p.startingPrice && <span className="text-gold font-semibold"> · from {p.startingPrice}</span>}
                </p>
                <div className="mt-3.5 flex flex-wrap gap-2">
                  <Link
                    to={`/admin/projects/${p.id}/edit`}
                    data-testid={`admin-project-edit-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-line hover:border-gold rounded-md px-3 py-2 transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </Link>
                  <button
                    onClick={() => update(p, { published: !p.published }, p.published ? "Unpublished — hidden from website." : "Published — live on website.")}
                    disabled={busy === p.id}
                    data-testid={`admin-project-publish-${p.id}`}
                    className={`inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md px-3 py-2 border transition-colors disabled:opacity-50 ${
                      p.published ? "text-ink-soft border-line hover:border-navy" : "text-navy bg-gold/20 border-gold/50 hover:bg-gold/35"
                    }`}
                  >
                    {p.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <button
                    onClick={() => update(p, { featured: !p.featured }, p.featured ? "Removed from homepage." : "Featured — shown on homepage.")}
                    disabled={busy === p.id}
                    data-testid={`admin-project-feature-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-line hover:border-gold rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    <Star size={12} /> {p.featured ? "Unfeature" : "Feature"}
                  </button>
                  <button
                    onClick={() => remove(p)}
                    disabled={busy === p.id}
                    data-testid={`admin-project-delete-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
